import { StatusCodes } from "http-status-codes";
import { Component, OnInit } from "@angular/core";
import { parse } from "papaparse";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Message } from "../../../models/message";
import { addMessage } from "../../../store/actions/message.actions";
import { UploadStatus, UPLOAD_STATUS } from "../../../models/uploadStatus.model";
import { DHLSyncService, UploadDHLLabel } from "../../../services/dhl-sync.service";
import { UserSaveJSON } from "../../../models/json/userSaveJSON";
import { MAX_LENGTH } from "../../../helpers/constants/fieldSize";
import { removeSpaces } from "../../../helpers/textHelper";
import { finishBusy, startBusy } from "src/app/store/actions/busy.actions";

type CSVLine = {
    barcode: string;
    receiverName: string;
    receiverStreetname: string;
    receiverHouseNumber: string;
    receiverHouseNumberAddition: string;
    receiverPostcode: string;
    receiverCity: string;
    receiverCountry: string;
    receiverEmail: string;
    receiverPhone: string;
    customerNumber: string;
    senderCompany: string;
    senderStreetname: string;
    senderHouseNumber: string;
    senderHouseNumberAddition: string;
    senderPostcode: string;
    senderCity: string;
    senderCountry: string;
    senderEmail: string;
    senderPhone: string;
    product: string;
    mark: string;
};

export enum CSV_UPLOAD_STATUS {
    NO_FILE_SELECTED,
    CSV_UPLOADING,
    CSV_UPLOADED,
    CSV_PARSED,
    CSV_IN_PROGRESS,
    ADDING_LABELS,
    LABELS_ADDED,
    CSV_UPLOAD_FAILED,
}

@Component({
    selector: "app-sync-dhl-labels",
    templateUrl: "./sync-dhl-labels.component.html",
    styleUrls: ["./sync-dhl-labels.component.scss"],
})
export class SyncDHLLabelsComponent implements OnInit {
    public status: CSV_UPLOAD_STATUS = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;

    public fileName = "";

    public labels: CSVLine[] = [];

    public isLoadingDetails: boolean = false;

    public latestUploadStatus: UploadStatus | null = null;

    public parsedRows: CSVLine[] = [];

    public currentLocalStatus: CSV_UPLOAD_STATUS = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;

    public date: string;

    public uploadStatus: UploadStatus | null = null;

    public UPLOAD_STATUSSES = UPLOAD_STATUS;

    public validationErrors: any;

    constructor(
        private backend: DHLSyncService,
        private router: Router,
        private store: Store<{ messages: Message[] }>,
    ) {}

    ngOnInit() {
        this.fetchLatestUploadStatus();
    }

    fetchLatestUploadStatus() {
        this.isLoadingDetails = true;
        this.backend.getLatestUploadStatus().subscribe({
            next: (status) => {
                this.latestUploadStatus = status;

                if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
                    this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                } else {
                    if (this.currentLocalStatus === CSV_UPLOAD_STATUS.CSV_IN_PROGRESS) {
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                    }
                }
                this.isLoadingDetails = false;
            },
            error: () => {
                this.latestUploadStatus = null;
                this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                this.isLoadingDetails = false;

                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Fout",
                            "Kon laatste upload status niet ophalen.",
                        ),
                    }),
                );
            },
        });
    }

    refreshSelectedStatus() {
        if (!this.latestUploadStatus) {
            this.fetchLatestUploadStatus();
            return;
        }

        const selectedId = this.latestUploadStatus.id;
        this.isLoadingDetails = true;
        this.backend.getUploadStatusById(selectedId).subscribe({
            next: (refreshedStatus) => {
                if (refreshedStatus) {
                    this.latestUploadStatus = refreshedStatus;

                    if (this.latestUploadStatus && !this.latestUploadStatus.isCompleted) {
                        this.currentLocalStatus = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                    } else {
                        if (this.currentLocalStatus === CSV_UPLOAD_STATUS.CSV_IN_PROGRESS) {
                            this.currentLocalStatus = CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
                        }
                        this.fileName = "";
                        this.parsedRows = [];
                        this.validationErrors = null;
                    }
                } else {
                    this.store.dispatch(
                        addMessage({
                            message: Message.createNotification(
                                "Status Niet Gevonden",
                                `Upload #${selectedId} niet meer gevonden. Lijst wordt vernieuwd.`,
                            ),
                        }),
                    );
                    this.fetchLatestUploadStatus();
                }
                this.isLoadingDetails = false;
            },
            error: () => {
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage(
                            "Fout",
                            "Kon geselecteerde status niet verversen.",
                        ),
                    }),
                );
                this.isLoadingDetails = false;
            },
        });
    }

    getRemainingAmount(status: UploadStatus | null): number {
        if (!status) return 0;
        return Math.max(
            0,
            status.totalAmount - status.successAmount - status.erroredAmount - status.skippedAmount,
        );
    }

    changeListener = (event: Event): void => {
        const target = <HTMLInputElement>event.target;
        const file = target.files[0];
        this.status = file ? CSV_UPLOAD_STATUS.CSV_UPLOADING : CSV_UPLOAD_STATUS.NO_FILE_SELECTED;
        if (this.status === CSV_UPLOAD_STATUS.CSV_UPLOADING) {
            this.readThis(event.target);
            this.fileName = file.name;
        }
    };

    readThis = (inputValue: any): void => {
        let result: { data: Array<{ postcode: string; barcode: string }> };
        const file: File = inputValue.files[0];
        const fileReader: FileReader = new FileReader();
        fileReader.onloadend = () => {
            const input = <string>fileReader.result;
            result = parse(input, {
                skipEmptyLines: true,
                header: true,
                dynamicTyping: false,
                transformHeader: (header: string) => header.split("*").join("").toLowerCase(),
            });
            this.labels = result.data.map((e) => ({
                barcode: e["track en trace code"],
                postcode: e["postcode ontvanger"],
                receiverName: e["ontvanger"] || "unknown",
                receiverStreetname: e["straatnaam ontvanger"] || "unknown",
                receiverHouseNumber: e["huisnummer ontvanger"],
                receiverHouseNumberAddition: e["toevoeging ontvanger"],
                receiverPostcode: e["postcode ontvanger"],
                receiverCity: e["plaatsnaam ontvanger"],
                receiverCountry: e["landcode ontvanger"],
                receiverEmail: e["e-mailadres ontvanger"] || "unknown@email.com",
                receiverPhone: e["telefoonnummer ontvanger"],
                customerNumber: e["klantnummer"],
                senderCompany: e["bedrijfsnaam verzender"],
                senderStreetname: e["straatnaam verzender"] || "unknown",
                senderHouseNumber: e["huisnummer verzender"] || "unknown",
                senderHouseNumberAddition: e["toevoeging verzender"],
                senderPostcode: e["postcode verzender"],
                senderCity: e["plaatsnaam verzender"],
                senderCountry: e["landcode verzender"],
                senderEmail: e["e-mailadres verzender"] || "unknown@email.com",
                senderPhone: e["telefoonnummer verzender"],
                mark: e["referenties"],
                product: e["product"],
            }));
            this.status = CSV_UPLOAD_STATUS.CSV_PARSED;
        };
        fileReader.readAsText(file);
    };

    get csvIsParsed(): boolean {
        return (
            this.status === CSV_UPLOAD_STATUS.CSV_PARSED ||
            this.status === CSV_UPLOAD_STATUS.ADDING_LABELS ||
            this.status === CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED
        );
    }

    get inProgress(): boolean {
        return (
            this.status === CSV_UPLOAD_STATUS.CSV_UPLOADING ||
            this.status === CSV_UPLOAD_STATUS.ADDING_LABELS
        );
    }

    get csvIsLoading(): boolean {
        return this.status === CSV_UPLOAD_STATUS.CSV_UPLOADING;
    }

    get csvInProgress(): boolean {
        return this.status === CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
    }

    buildInput = () => {
        this.status = CSV_UPLOAD_STATUS.ADDING_LABELS;
        let csvJSONarray: any = [];
        csvJSONarray = this.labels
            .filter((line: CSVLine) => line.barcode && line.receiverPostcode)
            .map((line: CSVLine): UploadDHLLabel => {
                let sender: UserSaveJSON | string = line.senderCompany;
                if (line.senderPostcode) {
                    sender = {
                        postcode: line.senderPostcode,
                        housenr: line.senderHouseNumber,
                        housenr_extra: line.senderHouseNumberAddition?.substring(
                            0,
                            MAX_LENGTH.HOUSENR_EXTRA,
                        ),
                        address_extra: "",
                        company_name: line.senderCompany?.substring(0, MAX_LENGTH.COMPANY_NAME),
                        city: line.senderCity,
                        country: line.senderCountry,
                        streetname: line.senderStreetname,
                        phone: line.senderPhone.substring(0, MAX_LENGTH.PHONE),
                        initials: "",
                        firstname: "",
                        middlename: "",
                        lastname: "Unknown",
                        email: removeSpaces(line.senderEmail),
                    };
                }
                return {
                    recipient: {
                        postcode: line.receiverPostcode,
                        housenr: line.receiverHouseNumber,
                        housenr_extra: line.receiverHouseNumberAddition?.substring(
                            0,
                            MAX_LENGTH.HOUSENR_EXTRA,
                        ),
                        address_extra: "",
                        company_name: "",
                        city: line.receiverCity,
                        country: line.receiverCountry,
                        streetname: line.receiverStreetname,
                        phone: line.receiverPhone.substring(0, MAX_LENGTH.PHONE),
                        initials: "",
                        firstname: "",
                        middlename: "",
                        lastname: line.receiverName.substring(0, MAX_LENGTH.LASTNAME),
                        email: removeSpaces(line.receiverEmail),
                    },
                    sender,
                    reference: line.barcode,
                    customer_number: line.customerNumber,
                    mark: line.mark,
                };
            });
        this.makeLabelBulk(csvJSONarray);
    };

    static colorRow = (rowNumber: any, status: string) => {
        const table = document.getElementById("table");
        const element = <HTMLTableRowElement>table.children[rowNumber];
        element.className = status;
    };

    close = () => {
        this.router.navigateByUrl("/dashboard");
    };

    makeLabelBulk = (labels: UploadDHLLabel[]) => {
        this.store.dispatch(startBusy());
        if (labels) {
            this.backend.syncDHLLabels(labels).subscribe({
                next: (d) => {
                    this.store.dispatch(finishBusy());
                    if (d.amount_to_upload > 0) {
                        this.status = CSV_UPLOAD_STATUS.CSV_IN_PROGRESS;
                        this.store.dispatch(
                            addMessage({
                                message: Message.createSuccessMessage(
                                    "Succes",
                                    `De CSV upload is bezig! ${
                                        labels.length
                                    } records worden toegevoegd. ${
                                        this.labels.length - labels.length
                                    } elementen waren niet correct. Je kan het modal sluiten.`,
                                ),
                            }),
                        );
                        this.fetchLatestUploadStatus();
                    }
                    if (d.status == UPLOAD_STATUS.STALLED) {
                        this.status = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
                    }
                },
                error: (e) => {
                    this.store.dispatch(finishBusy());
                    if (e.status === StatusCodes.UNPROCESSABLE_ENTITY && e.error?.errors) {
                        this.validationErrors = e.error.errors;
                    }

                    this.status = CSV_UPLOAD_STATUS.CSV_UPLOAD_FAILED;
                    this.store.dispatch(
                        addMessage({
                            message: Message.createErrorMessage("Fout", "De CSV upload is mislukt"),
                        }),
                    );
                },
            });
        } else {
            this.store.dispatch(
                addMessage({
                    message: Message.createErrorMessage(
                        "Fout",
                        "Er zijn geen correcte records gevonden in de CSV",
                    ),
                }),
            );
        }
    };

    handleStopSync(): void {
        if (!this.latestUploadStatus || this.latestUploadStatus.isCompleted) {
            this.store.dispatch(
                addMessage({
                    message: Message.createNotification(
                        "Geen actieve upload",
                        "Er is geen actieve upload om te stoppen.",
                    ),
                }),
            );
            return;
        }

        const uploadId = this.latestUploadStatus.id;
        this.store.dispatch(startBusy());
        this.backend.stopSync(uploadId).subscribe({
            next: () => {
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createSuccessMessage(
                            "Stopverzoek verzonden",
                            "Het verzoek om de upload te stoppen is verzonden.",
                        ),
                    }),
                );
                this.fetchLatestUploadStatus();
            },
            error: () => {
                this.store.dispatch(finishBusy());
                this.store.dispatch(
                    addMessage({
                        message: Message.createErrorMessage("Fout", "Kon de upload niet stoppen."),
                    }),
                );
            },
        });
    }
}
